// Session client: signaling (WebSocket via server, then DataChannel direct to the SFU),
// mediasoup transports, mic producer, consumers and the per-channel audio mix.
import { Device } from 'mediasoup-client';

const basePath = location.pathname.replace(/[^/]*$/, '');
const wsUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${basePath}ws`;

class Emitter {
  #h = {};
  on(ev, fn) {
    (this.#h[ev] ||= []).push(fn);
    return () => (this.#h[ev] = this.#h[ev].filter((f) => f !== fn));
  }
  emit(ev, ...a) {
    for (const fn of this.#h[ev] || []) fn(...a);
  }
}

/** short device label for the monitoring details */
function deviceName() {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad|Macintosh.*Mobile/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? 'iPad' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'Device';
  const br = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const ver = (ua.match(/(?:Version|Chrome|CriOS|Firefox|Edg)\/(\d+)/) || [])[1];
  return `${os} · ${br}${ver ? ` ${ver}` : ''}${matchMedia('(display-mode: standalone)').matches ? ' · Homescreen-App' : ''}`;
}

/**
 * Warm standby connection to the other engine of a hybrid session (cloud ↔ Mac on site): own WebSocket,
 * transports and DataChannel, no microphone, nothing to hear. When the active path dies (Mac quit, cloud restart)
 * the client takes this connection over ("activate") instead of building a new one – and it keeps working when
 * the cloud is gone, because its DataChannel runs directly to the Mac.
 */
class WarmLink {
  constructor({ token, sessionId, path, iceServers, onDead }) {
    Object.assign(this, { token, sessionId, path, iceServers, onDead });
    this.pending = new Map();
    this.seq = 0;
    this.ready = false;
    this.okAt = 0;
    this.closed = false;
    this.owner = null; // the SessionClient after the takeover
    this.sink = (msg) => this.#onMessage(msg);
    this.ws = new WebSocket(wsUrl);
    this.ws.onopen = () => this.ws.send(JSON.stringify({ t: 'auth', token }));
    this.ws.onmessage = (ev) => this.sink(JSON.parse(ev.data));
    this.ws.onclose = () => !this.ready && this.close('ws closed');
  }

  healthy() {
    return this.ready && !this.closed && Date.now() - this.okAt < 3500;
  }

  #send(msg) {
    if (this.dc && this.dcOut?.readyState === 'open') {
      try {
        this.dcOut.send(JSON.stringify(msg));
        return true;
      } catch {}
    }
    if (this.ws?.readyState === 1) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  request(method, data = {}, timeout = 6000) {
    if (this.owner) return this.owner.request(method, data, timeout);
    const id = ++this.seq;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => (this.pending.delete(id), reject(new Error(`timeout: ${method}`))), timeout);
      this.pending.set(id, { resolve, reject, timer });
      if (!this.#send({ t: 'req', id, method, data })) (clearTimeout(timer), this.pending.delete(id), reject(new Error('offline')));
    });
  }

  #onMessage(msg) {
    if (msg.t === 'auth-ok') this.ws.send(JSON.stringify({ t: 'join', sessionId: this.sessionId, path: this.path, warm: true }));
    else if (msg.t === 'joined') {
      this.peerId = msg.peerId;
      this.#setup().catch((e) => this.close(e.message));
    } else if (msg.t === 'res') {
      const p = this.pending.get(msg.id);
      if (!p) return;
      clearTimeout(p.timer);
      this.pending.delete(msg.id);
      msg.ok ? p.resolve(msg.data) : p.reject(new Error(msg.error));
    } else if (['join-fail', 'auth-fail', 'rejoin', 'base-moved', 'ended', 'kicked'].includes(msg.t)) this.close(msg.error || msg.t);
  }

  async #setup() {
    const init = await this.request('init');
    this.device = new Device();
    await this.device.load({ routerRtpCapabilities: init.routerRtpCapabilities });
    const { send, recv } = await this.request('createTransports');
    const relay = localStorage.getItem('talk.forceRelay') ? { iceTransportPolicy: 'relay' } : {};
    this.sendTransport = this.device.createSendTransport({ ...send, iceServers: this.iceServers, ...relay });
    this.recvTransport = this.device.createRecvTransport({ ...recv, iceServers: this.iceServers, ...relay });
    for (const t of [this.sendTransport, this.recvTransport]) {
      t.on('connect', ({ dtlsParameters }, ok, fail) => this.request('connectTransport', { transportId: t.id, dtlsParameters }).then(ok, fail));
      t.on('connectionstatechange', (st) => !this.owner && st === 'failed' && this.close('ice failed'));
    }
    this.sendTransport.on('produce', ({ kind, rtpParameters }, ok, fail) => this.request('produce', { transportId: this.sendTransport.id, kind, rtpParameters }).then(ok, fail));
    this.sendTransport.on('producedata', ({ sctpStreamParameters, label, protocol }, ok, fail) =>
      this.request('produceData', { transportId: this.sendTransport.id, sctpStreamParameters, label, protocol }).then(ok, fail),
    );
    this.dcOut = await this.sendTransport.produceData({ ordered: true, label: 'ctl' });
    const { dataConsumer } = await this.request('ready', { rtpCapabilities: this.device.rtpCapabilities });
    this.dcIn = await this.recvTransport.consumeData(dataConsumer);
    this.dcIn.on('message', (data) => this.sink(JSON.parse(data)));
    const whenOpen = (dc) => (dc.readyState === 'open' ? Promise.resolve() : new Promise((r) => dc.on('open', r)));
    await Promise.race([Promise.all([whenOpen(this.dcOut), whenOpen(this.dcIn)]), new Promise((_, rej) => setTimeout(() => rej(new Error('no direct path')), 8000))]);
    this.dc = true;
    await this.request('dcReady', {}, 3000);
    this.ready = true;
    this.okAt = Date.now();
    this.pingTimer = setInterval(() => {
      if (this.owner) return clearInterval(this.pingTimer);
      this.request('ping', {}, 1500).then(
        () => (this.okAt = Date.now()),
        () => Date.now() - this.okAt > 10000 && this.close('no answer'),
      );
    }, 1000);
  }

  /** closes everything unless the client took it over */
  close(why = '') {
    if (this.closed || this.owner) return;
    this.closed = true;
    clearInterval(this.pingTimer);
    try {
      this.#send({ t: 'req', id: ++this.seq, method: 'leave', data: {} });
    } catch {}
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.ws.onclose = null;
    try {
      this.ws.close();
    } catch {}
    this.onDead?.(this, why);
  }
}

export class SessionClient extends Emitter {
  constructor({ token, sessionId }) {
    super();
    this.token = token;
    this.sessionId = sessionId;
    this.peerId = null;
    this.ws = null;
    this.pending = new Map();
    this.seq = 0;
    this.closed = false;
    this.iceServers = [];
    this.state = { channels: [], roles: [], peers: [], program: {} };
    this.me = null;
    this.tally = { cams: [] };
    this.status = { ws: false, dc: false, media: 'idle' };
    this.consumers = new Map(); // consumerId -> { consumer, gain, el, appData }
    this.vol = JSON.parse(localStorage.getItem('talk.vol') || '{}'); // channelId -> {v, muted}
    // playout buffer for the program feed only (talkback stays as direct as possible)
    this.programBuffer = Number(localStorage.getItem('talk.pgmBuffer') ?? 300);
    this.vol.__program ||= { v: 0.6, muted: false };
    this.vol.__direct ||= { v: 1, muted: false };
    this.talking = new Set();
    this.setupBusy = false;
    this.path = null; // 'lan' (directly to the Mac on site) or 'cloud'
    this.forcePath = null;
    this.downSince = 0;
    this.warm = null; // WarmLink to the other engine (hybrid)
    this.warmRetryAt = 0;
    this.warmFails = 0;
    this.aliveAt = 0; // last answer from the active engine
  }

  // ---------- lifecycle ----------
  async start({ withMic = true, micDeviceId = null, outputDeviceId = null } = {}) {
    this.ctx ||= new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
    this.master ||= this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    await this.ctx.resume().catch(() => {});
    if (localStorage.getItem('talk.testTone')) {
      // diagnostics: a test tone instead of the microphone
      const osc = this.ctx.createOscillator();
      const dest = this.ctx.createMediaStreamDestination();
      osc.frequency.value = 660;
      osc.connect(dest);
      osc.start();
      this.mic = dest.stream;
    } else if (withMic) {
      this.micDeviceId = micDeviceId;
      this.mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, ...(micDeviceId ? { deviceId: { exact: micDeviceId } } : {}) },
      });
    }
    this.rawMic = this.mic?.getAudioTracks()[0] || null;
    this.micTrack = this.rawMic;
    if (this.micTrack) this.micTrack.enabled = false;
    await this.setSquelch(Number(localStorage.getItem('talk.squelch') || 0)).catch((e) => console.warn('squelch unavailable', e));
    if (outputDeviceId) await this.setOutput(outputDeviceId).catch(() => {});
    // own mic level (shown while talking): analyser on a clone, independent of track.enabled
    if (this.mic) {
      const src = this.ctx.createMediaStreamSource(new MediaStream([this.mic.getAudioTracks()[0].clone()]));
      this.micAnalyser = this.ctx.createAnalyser();
      this.micAnalyser.fftSize = 512;
      src.connect(this.micAnalyser);
    }
    this.statsTimer = setInterval(() => this.#reportStats().catch(() => {}), 2000);
    this.startedAt = Date.now();
    this.#connect();
    this.pingTimer = setInterval(() => this.request('ping').catch(() => {}), 5000);
    this.pathTimer = setInterval(() => this.#checkPath(), 1000);
    // watchdog: the active engine answers every second, otherwise talking moves to the warm connection
    this.aliveTimer = setInterval(() => this.#watchdog(), 1000);
  }

  #watchdog() {
    this.#checkDirect();
    if (this.closed || this.status.media !== 'connected' || this.setupBusy || this.promoting) return;
    this.request('ping', {}, 1200).then(
      () => (this.aliveAt = Date.now()),
      () => {},
    );
    const silent = this.aliveAt && Date.now() - this.aliveAt > 2500;
    if (silent && this.warm?.healthy()) this.#promote('auto');
    // no warm connection: the Mac is gone but the cloud is there → rebuild via the cloud
    else if (silent && this.canSwitch && this.path === 'lan' && this.status.ws && Date.now() - this.aliveAt > 4000) this.switchPath('cloud', 'auto');
    this.#keepWarm();
  }

  /**
   * direct path (2.2.3): the cloud is gone for 3 s → talk to the Mac on its own name (wss://h…lan…:7443), with the
   * ticket the cloud gave us at join. Back to the cloud as soon as it answers again. Needs the venue Wi-Fi.
   */
  #checkDirect() {
    if (this.closed) return;
    const now = Date.now();
    this.wsDownSince = this.status.ws ? 0 : this.wsDownSince || now;
    if (this.onDirect) {
      if (now - (this.cloudProbeAt || 0) < 10000) return;
      this.cloudProbeAt = now;
      fetch(`${basePath}api/health`, { cache: 'no-store', signal: AbortSignal.timeout(4000) })
        .then((r) => r.ok && this.onDirect && !this.closed && this.#leaveDirect('cloud back'))
        .catch(() => {});
      return;
    }
    if (!this.direct || this.status.ws || !this.wsDownSince || now - this.wsDownSince < 3000 || now < (this.directRetryAt || 0) || localStorage.getItem('talk.noDirect')) return;
    this.onDirect = true;
    this.directOpened = false;
    this.lastSwitch = { at: now, from: this.path, to: 'direct', why: 'cloud gone' };
    const old = this.ws;
    this.ws = null;
    try {
      old?.close();
    } catch {}
    this.warm?.close('direct');
    this.warm = null;
    this.#connect();
  }

  #leaveDirect(why) {
    this.onDirect = false;
    if (why !== 'cloud back') this.directRetryAt = Date.now() + 30000;
    this.lastSwitch = { at: Date.now(), from: 'direct', to: 'cloud', why };
    const old = this.ws;
    this.ws = null;
    try {
      old?.close();
    } catch {}
    this.#setStatus({ ws: false, direct: false });
    this.#connect();
  }

  /** hybrid: keep a standby connection to the other engine (retries with growing pauses) */
  #keepWarm() {
    if (!this.canSwitch || !this.status.ws || this.warm || Date.now() < this.warmRetryAt || localStorage.getItem('talk.noWarm')) return;
    const path = this.path === 'lan' ? 'cloud' : 'lan';
    this.warm = new WarmLink({
      token: this.token,
      sessionId: this.sessionId,
      path,
      iceServers: this.iceServers,
      onDead: (w, why) => {
        if (this.warm !== w) return;
        this.warm = null;
        this.warmFails++;
        // the Mac is only reachable from its network: ask rarely when it says so
        this.warmRetryAt = Date.now() + (why === 'No standby path' ? 60000 : Math.min(60000, 3000 * 2 ** Math.min(this.warmFails, 5)));
        this.emit('warm', null);
      },
    });
    const w = this.warm;
    const t = setInterval(() => {
      if (this.warm !== w) return clearInterval(t);
      if (w.ready) {
        clearInterval(t);
        this.warmFails = 0;
        this.emit('warm', w.path);
      }
    }, 300);
  }

  /** take the warm connection over: activate it on its engine, drop the old one (no new transports needed) */
  async #promote(why) {
    const w = this.warm;
    if (!w || this.promoting) return false;
    this.promoting = true;
    this.warm = null;
    const from = this.path;
    try {
      // drop the old connection first (its consumers would collide with the new ones)
      const oldWs = this.ws;
      const msg = JSON.stringify({ t: 'req', id: ++this.seq, method: 'leave', data: {} });
      try {
        if (this.dcOut?.readyState === 'open') this.dcOut.send(msg);
        else oldWs?.send(msg);
      } catch {}
      this.ws = null;
      if (oldWs) {
        oldWs.onclose = null;
        try {
          oldWs.close();
        } catch {}
      }
      this.#teardownMedia();
      // take over its pieces
      w.owner = this;
      w.sink = (m) => this.#onMessage(m, 'dc');
      this.device = w.device;
      this.sendTransport = w.sendTransport;
      this.recvTransport = w.recvTransport;
      this.dcOut = w.dcOut;
      this.dcIn = w.dcIn;
      for (const t of [this.sendTransport, this.recvTransport]) this.#watchTransport(t);
      for (const ev of ['close', 'error']) this.dcIn.on(ev, () => this.dcIn === w.dcIn && this.#setStatus({ dc: false }));
      this.peerId = w.peerId;
      this.path = w.path;
      this.status.dc = true;
      this.#attachWs(w.ws);
      const { me } = await this.request('activate', {}, 3000);
      this.me = me;
      this.emit('me', me);
      this.#wsSend({ t: 'activated' });
      this.aliveAt = Date.now();
      this.lastSwitch = { at: Date.now(), from, to: this.path, why: why === 'auto' ? 'warm' : why };
      this.#setStatus({ dc: true, media: 'connected', path: this.path, ws: this.ws?.readyState === 1 });
      await this.#produceMic();
      this.emit('path-switch', this.lastSwitch);
      return true;
    } catch (e) {
      console.warn('takeover failed', e);
      // classic way: join again (needs the cloud)
      this.status.dc = false;
      this.#setStatus({ media: 'failed' });
      if (this.ws?.readyState === 1) this.switchPath(this.path === 'lan' ? 'lan' : 'cloud', 'auto');
      else if (!this.ws) this.#connect();
      return false;
    } finally {
      this.promoting = false;
    }
  }

  /** join message: keep a working path, otherwise let the cloud pick (same network as the Mac → on site) */
  #joinMsg() {
    const keep = this.status.media === 'connected' && this.path;
    return { t: 'join', sessionId: this.sessionId, resumePeerId: this.peerId, path: keep ? this.path : this.forcePath || 'auto' };
  }

  /** hybrid: on site but the Mac is out of reach (phone left the Wi-Fi, Mac gone) → continue via the cloud by itself */
  #checkPath() {
    if (this.closed || !this.canSwitch) return;
    const down = this.status.media !== 'connected';
    this.downSince = down ? this.downSince || Date.now() : 0;
    if (this.path === 'lan' && down && this.status.ws && !this.setupBusy && Date.now() - this.downSince > 6000) this.switchPath('cloud', 'auto');
  }

  /** move to the other engine of a hybrid session ('lan' | 'cloud' | 'auto'); talk state and volumes stay */
  switchPath(path, why = 'manual') {
    if (this.closed || !this.canSwitch) return false;
    if (this.warm?.healthy() && this.warm.path === path) {
      this.forcePath = path;
      this.#promote(why);
      return true;
    }
    if (!this.status.ws) return false;
    this.forcePath = path === 'auto' ? null : path;
    this.lastSwitch = { at: Date.now(), from: this.path, to: path, why };
    this.#wsSend({ t: 'req', id: ++this.seq, method: 'leave', data: {} });
    this.#teardownMedia();
    this.warm?.close('switch');
    this.warm = null;
    this.warmRetryAt = Date.now() + 5000;
    this.peerId = null;
    this.downSince = 0;
    this.#wsSend({ t: 'join', sessionId: this.sessionId, resumePeerId: null, path });
    this.emit('path-switch', this.lastSwitch);
    return true;
  }

  /** Fire-and-forget leave for page unload (no awaiting possible). */
  leaveNow() {
    this.warm?.close('leave');
    const msg = JSON.stringify({ t: 'req', id: ++this.seq, method: 'leave', data: {} });
    try {
      if (this.dcOut?.readyState === 'open') this.dcOut.send(msg);
      else this.ws?.send(msg);
    } catch {}
    this.closed = true;
  }

  /** Output device (headset/speaker) where the browser supports it (Chrome/Edge; not iOS Safari). */
  static canSelectOutput() {
    return typeof AudioContext !== 'undefined' && 'setSinkId' in AudioContext.prototype;
  }
  async setOutput(deviceId) {
    if (!SessionClient.canSelectOutput()) return;
    await this.ctx.setSinkId(deviceId === 'default' ? '' : deviceId);
    this.outputDeviceId = deviceId;
  }

  /**
   * Squelch (noise gate) for the own microphone: dB threshold (-90…-20), 0 = off.
   * The gate sits between the raw mic and the sent track; switching swaps the producer track in place.
   */
  async setSquelch(db) {
    this.squelch = Number(db) || 0;
    localStorage.setItem('talk.squelch', String(this.squelch));
    if (!this.rawMic) return;
    let next = this.rawMic;
    if (this.squelch) {
      if (!this.gate) {
        await this.ctx.audioWorklet.addModule('gate-worklet.js');
        this.gate = new AudioWorkletNode(this.ctx, 'ot-gate', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
        this.gate.port.onmessage = (e) => (this.gateOpen = !!e.data);
        // the raw track stays enabled as the gate's source; talk on/off switches the gated track
        this.ctx.createMediaStreamSource(new MediaStream([this.rawMic])).connect(this.gate);
        this.gateDest = this.ctx.createMediaStreamDestination();
        this.gate.connect(this.gateDest);
      }
      this.gate.parameters.get('threshold').setValueAtTime(this.squelch, this.ctx.currentTime);
      next = this.gateDest.stream.getAudioTracks()[0];
      this.rawMic.enabled = true;
    }
    if (next === this.micTrack) return;
    next.enabled = this.talking.size > 0;
    if (next === this.rawMic) next.enabled = this.talking.size > 0;
    await this.producer?.replaceTrack({ track: next });
    this.micTrack = next;
  }

  /** 0..1 level of the own microphone */
  micLevel() {
    if (!this.micAnalyser) return 0;
    const buf = new Float32Array(this.micAnalyser.fftSize);
    this.micAnalyser.getFloatTimeDomainData(buf);
    let peak = 0;
    for (const v of buf) peak = Math.max(peak, Math.abs(v));
    return Math.min(1, peak * 1.4);
  }

  /** page goes to the background: never keep sending the mic (talk keys are released, the producer pauses) */
  background() {
    if (this.closed) return;
    if (this.talking.size) {
      this.talking.clear();
      this.#sendTalk();
    }
    if (this.micTrack) this.micTrack.enabled = false;
    this.producer?.pause();
  }

  /** after the page was hidden (iOS stops audio and may end the mic): resume audio and rebuild media */
  async recover(hiddenMs = 0) {
    this.ctx?.resume().catch(() => {});
    for (const c of this.consumers?.values?.() || []) c.el?.play?.().catch(() => {});
    if (this.closed) return;
    // iOS ends the getUserMedia track in the background → open the mic again
    if (this.rawMic && this.rawMic.readyState === 'ended' && !localStorage.getItem('talk.testTone')) await this.#reopenMic().catch((e) => console.warn('mic reopen failed', e));
    this.producer?.resume();
    // a socket that slept for long may look open but be dead (iOS) → reconnect; the session resumes
    if (this.ws?.readyState !== 1 || hiddenMs > 15000) {
      this.ws?.close();
      return;
    }
    const bad = !this.sendTransport || ['failed', 'disconnected', 'closed'].includes(this.recvTransport?.connectionState) || ['failed', 'disconnected', 'closed'].includes(this.sendTransport?.connectionState);
    // longer in the background: transports often look fine but carry nothing any more → rebuild
    if (bad || hiddenMs > 1500) this.#setupMedia();
  }

  async #reopenMic() {
    const id = this.micDeviceId;
    this.mic = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, ...(id ? { deviceId: { exact: id } } : {}) },
    });
    this.rawMic = this.mic.getAudioTracks()[0];
    this.micTrack = this.rawMic;
    this.micTrack.enabled = false;
    this.gate?.disconnect();
    this.gate = this.gateDest = null;
    await this.setSquelch(this.squelch).catch(() => {});
    await this.producer?.replaceTrack({ track: this.micTrack }).catch(() => {});
    const src = this.ctx.createMediaStreamSource(new MediaStream([this.rawMic.clone()]));
    this.micAnalyser = this.ctx.createAnalyser();
    this.micAnalyser.fftSize = 512;
    src.connect(this.micAnalyser);
  }

  signal(target) {
    return this.request('signal', { target });
  }

  async #reportStats() {
    const pc = this.recvTransport?._handler?._pc;
    const spc = this.sendTransport?._handler?._pc;
    if (!pc || this.status.media !== 'connected') return;
    const st = await pc.getStats();
    let pair = null;
    let inBytes = 0, lost = 0, recv = 0, jitter = 0, jn = 0, local = null, remote = null, video = null, conceal = 0, silent = 0, samples = 0, aBytes = 0;
    st.forEach((r) => {
      if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') pair = r;
      if (r.type === 'inbound-rtp') {
        inBytes += r.bytesReceived || 0;
        lost += r.packetsLost || 0;
        recv += r.packetsReceived || 0;
        if (r.kind === 'audio' && r.jitter != null && r.packetsReceived) (jitter += r.jitter), jn++;
        if (r.kind === 'audio') (conceal += r.concealedSamples || 0), (silent += r.silentConcealedSamples || 0), (samples += r.totalSamplesReceived || 0), (aBytes += r.bytesReceived || 0);
        if (r.kind === 'video' && r.framesDecoded) video = { w: r.frameWidth, h: r.frameHeight, fps: Math.round(r.framesPerSecond || 0), freezes: r.freezeCount || 0, dropped: r.framesDropped || 0 };
      }
    });
    if (pair) st.forEach((r) => (r.id === pair.localCandidateId && (local = r), r.id === pair.remoteCandidateId && (remote = r)));
    let outBytes = 0;
    if (spc) (await spc.getStats()).forEach((r) => r.type === 'outbound-rtp' && (outBytes += r.bytesSent || 0));
    const now = performance.now();
    const p = this.prevStats;
    this.prevStats = { now, inBytes, outBytes, lost, recv, conceal, silent, samples, aBytes };
    if (!p) return;
    const dt = (now - p.now) / 1000;
    const dLost = Math.max(0, lost - p.lost);
    const dRecv = Math.max(0, recv - p.recv);
    this.request('stats', {
      rtt: pair?.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : null,
      jitter: jn ? Math.round((jitter / jn) * 1000) : null,
      loss: dRecv + dLost ? Math.round((dLost / (dRecv + dLost)) * 1000) / 10 : 0,
      inKbps: Math.round(((inBytes - p.inBytes) * 8) / dt / 1000),
      outKbps: Math.round(((outBytes - p.outBytes) * 8) / dt / 1000),
      path: local ? `${local.candidateType}/${local.relayProtocol || local.protocol}` : '',
      // snapshot for the monitoring details (not graphed)
      info: {
        device: deviceName(),
        visible: document.visibilityState === 'visible',
        net: navigator.connection?.type || navigator.connection?.effectiveType || '',
        local: local ? `${local.candidateType} · ${local.protocol}${local.networkType && local.networkType !== 'unknown' ? ` · ${local.networkType}` : ''}` : '',
        remote: remote ? `${remote.address || remote.ip || '?'}:${remote.port} (${remote.candidateType})` : '',
        // real dropouts only: concealment minus silence filled in while nobody talks, and only in intervals with audio
        conceal: samples - p.samples > 0 && aBytes - p.aBytes > 2000 ? Math.round((Math.max(0, conceal - p.conceal - (silent - p.silent)) / (samples - p.samples)) * 1000) / 10 : null,
        video: video ? `${video.w}×${video.h} · ${video.fps} fps · ${video.freezes} freezes · ${video.dropped} dropped` : '',
        dc: !!this.status.dc,
        squelch: this.squelch || 0,
        talking: this.talking.size > 0,
        since: this.startedAt || null,
        buffer: this.programBuffer || 0,
        // availability: active engine, warm standby to the other one, last path change
        route: this.onDirect ? 'direct' : this.path || '',
        standby: this.warm ? `${this.warm.path} · ${this.warm.healthy() ? 'ready' : 'connecting'}` : '',
        switched: this.lastSwitch ? `${this.lastSwitch.from || '?'} → ${this.lastSwitch.to} (${this.lastSwitch.why}) ${new Date(this.lastSwitch.at).toLocaleTimeString()}` : '',
      },
    }, 3000).catch(() => {});
  }

  async leave() {
    this.closed = true;
    clearInterval(this.pingTimer);
    clearInterval(this.statsTimer);
    clearInterval(this.pathTimer);
    clearInterval(this.aliveTimer);
    this.warm?.close('leave');
    this.warm = null;
    await this.request('leave', {}, 2000).catch(() => {});
    this.#teardownMedia();
    this.ws?.close();
    this.mic?.getTracks().forEach((t) => t.stop());
    this.ctx?.close();
  }

  #setStatus(patch) {
    Object.assign(this.status, patch);
    this.emit('status', this.status);
  }

  // ---------- WebSocket path ----------
  #connect() {
    if (this.closed) return;
    if (this.onDirect) {
      let ws;
      try {
        ws = new WebSocket(this.direct.url);
      } catch {
        return this.#leaveDirect('direct unreachable');
      }
      ws.onopen = () => ((this.directOpened = true), ws.send(JSON.stringify({ t: 'auth', ticket: this.direct.ticket })));
      // never opened (other network, router blocks the name, browser said no): back to the cloud attempts
      ws.addEventListener('close', () => this.ws === ws && !this.directOpened && this.#leaveDirect('direct unreachable'));
      this.#attachWs(ws);
      return;
    }
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => ws.send(JSON.stringify({ t: 'auth', token: this.token }));
    this.#attachWs(ws);
  }

  /** the WebSocket to the cloud (own or taken over from the warm connection) */
  #attachWs(ws) {
    this.ws = ws;
    if (ws.readyState > 1) {
      // already gone (cloud down): try again shortly, the DataChannel carries on meanwhile
      this.#setStatus({ ws: false });
      this.ws = null;
      if (!this.closed) setTimeout(() => !this.ws && this.#connect(), 2000);
      return;
    }
    // heartbeat: a half-open connection (e.g. after a server restart behind the proxy) is detected within ~20 s
    let lastHb = Date.now();
    clearInterval(this.hbTimer);
    this.hbTimer = setInterval(() => {
      if (this.ws !== ws || ws.readyState !== 1) return;
      if (Date.now() - lastHb > 20000) {
        // don't wait for a close handshake that will never come: abandon and reconnect now
        this.ws = null;
        try {
          ws.close();
        } catch {}
        this.#setStatus({ ws: false });
        return this.#connect();
      }
      ws.send(JSON.stringify({ t: 'hb' }));
    }, 10000);
    ws.addEventListener('message', () => (lastHb = Date.now()));
    ws.onmessage = (ev) => this.#onMessage(JSON.parse(ev.data), 'ws');
    if (ws.readyState === 1) this.#setStatus({ ws: true });
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.#setStatus({ ws: false });
      if (!this.closed) setTimeout(() => this.#connect(), 2000);
    };
  }

  #wsSend(msg) {
    if (this.ws?.readyState === 1) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  request(method, data = {}, timeout = 8000) {
    const id = ++this.seq;
    const msg = { t: 'req', id, method, data };
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`timeout: ${method}`));
      }, timeout);
      this.pending.set(id, { resolve, reject, timer });
      let sent = false;
      if (this.dcOut?.readyState === 'open' && this.status.dc) {
        try {
          this.dcOut.send(JSON.stringify(msg));
          sent = true;
        } catch {}
      }
      if (!sent && !this.#wsSend(msg)) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(new Error('offline'));
      }
    });
  }

  async #onMessage(msg, via) {
    switch (msg.t) {
      case 'auth-ok':
        this.iceServers = msg.iceServers || [];
        this.user = msg.user;
        this.#wsSend(this.#joinMsg());
        break;
      case 'auth-fail':
        if (this.onDirect) return this.#leaveDirect('ticket refused');
        this.emit('fatal', 'Sign-in expired – please sign in again');
        this.closed = true;
        break;
      case 'join-fail':
        if (this.onDirect && !msg.fatal) return this.#leaveDirect('direct join refused');
        if (msg.fatal) {
          this.closed = true;
          return this.emit('fatal', msg.error);
        }
        // already joined once (server/host restart) or direct path alive: keep retrying for a while
        // also covers joining right while the server/host restarts (session is re-announced within seconds)
        if (this.status.dc || (this.joinedOnce && Date.now() - this.joinedOnce < 120000) || Date.now() - this.startedAt < 20000) {
          this.#setStatus({ ws: false, waiting: msg.error });
          setTimeout(() => this.ws?.close(), 3000);
        } else this.emit('fatal', msg.error);
        break;
      case 'joined':
        this.peerId = msg.peerId;
        this.session = msg.session;
        this.joinedOnce = Date.now();
        if (msg.direct) this.direct = msg.direct; // from the cloud: how to reach the Mac without it
        this.path = msg.path || (msg.session?.mode === 'global' ? 'cloud' : 'lan');
        this.canSwitch = !!msg.path && msg.session?.mode === 'hybrid'; // only the cloud can move us between the two engines
        this.#setStatus({ ws: true, waiting: null, path: this.path, direct: !!this.onDirect });
        this.emit('joined', msg.session);
        if (!msg.resumed || this.status.media !== 'connected') this.#setupMedia();
        break;
      case 'res': {
        const p = this.pending.get(msg.id);
        if (!p) return;
        clearTimeout(p.timer);
        this.pending.delete(msg.id);
        msg.ok ? p.resolve(msg.data) : p.reject(new Error(msg.error));
        break;
      }
      case 'state':
        this.state = msg;
        this.#updateGains();
        this.emit('state', msg);
        break;
      case 'me':
        this.me = msg.me;
        this.emit('me', msg.me);
        this.#updateGains();
        break;
      case 'tally':
        this.tally = msg.tally;
        this.emit('tally', msg.tally);
        break;
      case 'newConsumer':
        this.#consume(msg).catch((e) => console.warn('consume failed', e));
        break;
      case 'consumerClosed':
        this.#closeConsumer(msg.consumerId);
        break;
      case 'hb':
        break;
      case 'base-moved':
        // cluster: another Mac runs the session now – join again, the cloud picks the path to it
        if (this.canSwitch) this.switchPath('auto', 'moved');
        else if (this.status.ws) (this.#teardownMedia(), this.#wsSend({ t: 'join', sessionId: this.sessionId, resumePeerId: null, path: 'auto' }));
        break;
      case 'lan-available':
        // the cloud sees us in the network of the Mac that runs the session (again): go back to talking to it directly
        if (this.path === 'cloud' && this.canSwitch) this.switchPath(this.warm?.path === 'lan' && this.warm.healthy() ? 'lan' : 'auto', 'lan-back');
        break;
      case 'base-lost':
        // the cloud lost the Mac: if our direct path to it is gone as well, continue via the cloud right away
        // (if only the Mac's internet is down, the direct path still answers and we stay on site)
        if (this.path === 'lan' && this.canSwitch && this.warm?.path === 'cloud' && this.warm.healthy())
          this.request('ping', {}, 1500).then(
            () => {},
            () => this.path === 'lan' && this.#promote('auto'),
          );
        else if (this.path === 'lan' && this.canSwitch)
          this.request('ping', {}, 1500).then(
            () => {},
            () => this.path === 'lan' && this.switchPath('cloud', 'auto'),
          );
        break;
      case 'rejoin':
        // the SFU no longer knows us (host/server restart): join again and rebuild media
        if (Date.now() - (this.lastRejoin || 0) > 3000) {
          this.lastRejoin = Date.now();
          this.status.media = 'idle';
          this.#setStatus({ dc: false });
          this.#wsSend(this.#joinMsg());
        }
        break;
      case 'signal':
        this.lastSignal = { from: msg.from?.name || '', fromId: msg.from?.id || null, target: msg.target, label: msg.label || '', at: Date.now() };
        this.emit('signal', msg);
        break;
      case 'kicked':
        this.closed = true;
        this.emit('kicked');
        break;
      case 'ended':
        this.closed = true;
        this.emit('ended');
        break;
      case 'error':
        console.warn('server error', msg.error);
        break;
    }
  }

  // ---------- media ----------
  #teardownMedia() {
    for (const id of [...this.consumers.keys()]) this.#closeConsumer(id);
    this.sendTransport?.close();
    this.recvTransport?.close();
    this.sendTransport = this.recvTransport = this.producer = this.dcOut = this.dcIn = null;
    this.#setStatus({ dc: false, media: 'idle' });
  }

  async #setupMedia() {
    if (this.setupBusy) return;
    this.setupBusy = true;
    try {
      this.#teardownMedia();
      this.#setStatus({ media: 'connecting' });
      const init = await this.request('init');
      this.me = init.me;
      this.tally = init.tally || this.tally;
      this.emit('me', this.me);
      this.emit('tally', this.tally);
      this.device = new Device();
      await this.device.load({ routerRtpCapabilities: init.routerRtpCapabilities });
      const { send, recv } = await this.request('createTransports');

      this.sendTransport = this.device.createSendTransport({ ...send, iceServers: this.iceServers, ...(localStorage.getItem('talk.forceRelay') ? { iceTransportPolicy: 'relay' } : {}) });
      this.recvTransport = this.device.createRecvTransport({ ...recv, iceServers: this.iceServers, ...(localStorage.getItem('talk.forceRelay') ? { iceTransportPolicy: 'relay' } : {}) });
      for (const t of [this.sendTransport, this.recvTransport]) {
        t.on('connect', ({ dtlsParameters }, ok, fail) => this.request('connectTransport', { transportId: t.id, dtlsParameters }).then(ok, fail));
        this.#watchTransport(t);
      }
      this.sendTransport.on('produce', ({ kind, rtpParameters }, ok, fail) =>
        this.request('produce', { transportId: this.sendTransport.id, kind, rtpParameters }).then(ok, fail),
      );
      this.sendTransport.on('producedata', ({ sctpStreamParameters, label, protocol }, ok, fail) =>
        this.request('produceData', { transportId: this.sendTransport.id, sctpStreamParameters, label, protocol }).then(ok, fail),
      );

      if (this.micTrack) await this.#produceMic(false);
      this.dcOut = await this.sendTransport.produceData({ ordered: true, label: 'ctl' });

      const { dataConsumer } = await this.request('ready', { rtpCapabilities: this.device.rtpCapabilities });
      this.dcIn = await this.recvTransport.consumeData(dataConsumer);
      this.dcIn.on('message', (data) => this.#onMessage(JSON.parse(data), 'dc'));
      const whenOpen = (dc) => (dc.readyState === 'open' ? Promise.resolve() : new Promise((r) => dc.on('open', r)));
      await Promise.all([whenOpen(this.dcOut), whenOpen(this.dcIn)]);
      // confirm the direct path end-to-end: ask over DC, answer comes back over DC
      this.status.dc = true;
      await this.request('dcReady');
      // the round trip over the data channel proves both transports end to end – some browsers (iOS Safari)
      // never report 'connected' on the transport itself, which left the pill on "verbinde …"
      this.#setStatus({ dc: true, media: 'connected' });
      const myDc = this.dcIn;
      for (const ev of ['close', 'error']) this.dcIn.on(ev, () => this.dcIn === myDc && this.#setStatus({ dc: false }));
      this.aliveAt = Date.now();
      await this.#afterMedia();
    } catch (e) {
      console.warn('media setup failed', e);
      this.status.dc = false;
      this.#setStatus({ media: 'failed' });
      this.emit('error', `Connection failed: ${e.message}`);
      setTimeout(() => !this.closed && this.status.ws && this.status.media === 'failed' && this.#setupMedia(), 4000);
    } finally {
      this.setupBusy = false;
    }
  }

  #watchTransport(t) {
    t.on('connectionstatechange', (st) => {
      if (t !== this.sendTransport && t !== this.recvTransport) return;
      if (st === 'connected' && this.sendTransport.connectionState === 'connected' && this.recvTransport.connectionState === 'connected') this.#setStatus({ media: 'connected' });
      if (st === 'failed' || st === 'disconnected' || st === 'closed') {
        // a warm connection to the other engine takes over at once
        if (this.warm?.healthy()) return void this.#promote('auto');
        // direct path is down: route control over the WebSocket until media is rebuilt
        this.#setStatus({ media: st === 'failed' ? 'failed' : 'reconnecting', dc: false });
        clearTimeout(this.iceTimer);
        this.iceTimer = setTimeout(() => {
          if (t.connectionState !== 'connected' && !this.closed && this.status.ws) this.#setupMedia();
        }, st === 'failed' ? 500 : 6000);
      }
    });
  }

  /** microphone producer (optionally followed by the talk / program state of this device) */
  async #produceMic(after = true) {
    if (this.micTrack)
      this.producer = await this.sendTransport.produce({
        track: this.micTrack,
        stopTracks: false,
        codecOptions: { opusDtx: true, opusFec: true, opusStereo: false, opusMaxAverageBitrate: 32000 },
      });
    if (after) await this.#afterMedia();
  }

  async #afterMedia() {
    if (this.talking.size) this.#sendTalk();
    if (this.videoQuality) this.setProgramVideo(this.videoQuality);
    this.request('programAudio', { on: true }).catch(() => {}); // muting is local (keeps the video running)
  }

  async #consume({ consumerId, producerId, kind, rtpParameters, appData }) {
    if (!this.recvTransport) return;
    const consumer = await this.recvTransport.consume({ id: consumerId, producerId, kind, rtpParameters, appData });
    const stream = new MediaStream([consumer.track]);
    const entry = { consumer, appData };
    if (appData.program || kind === 'video') this.#applyBuffer(consumer);
    if (kind === 'audio') {
      // muted element keeps Chrome pulling the remote track; sound goes through WebAudio (works on iOS)
      const el = new Audio();
      el.muted = true;
      el.srcObject = stream;
      el.play().catch(() => {});
      const src = this.ctx.createMediaStreamSource(stream);
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      src.connect(gain).connect(this.master);
      Object.assign(entry, { el, src, gain });
    } else {
      entry.stream = stream;
      this.videoConsumer = entry;
      this.emit('video', stream);
      await this.request('resumeConsumer', { consumerId });
    }
    this.consumers.set(consumerId, entry);
    this.#updateGains();
  }

  #closeConsumer(id) {
    const e = this.consumers.get(id);
    if (!e) return;
    this.consumers.delete(id);
    e.consumer.close();
    e.src?.disconnect();
    e.gain?.disconnect();
    if (e.el) e.el.srcObject = null;
    if (this.videoConsumer === e) {
      this.videoConsumer = null;
      this.emit('video', null);
    }
  }

  // ---------- audio mix ----------
  volume(key) {
    return this.vol[key] || { v: 0.8, muted: false };
  }

  setVolume(key, patch) {
    this.vol[key] = { ...this.volume(key), ...patch };
    localStorage.setItem('talk.vol', JSON.stringify(this.vol));
    // program audio is muted here only: pausing it on the server would stall the video (lip sync waits for audio)
    this.#updateGains();
  }

  /** someone I hear is talking right now (for program ducking) and whether that's on a priority channel */
  #activity() {
    let any = false;
    let priority = false;
    const pri = new Set(this.state.channels.filter((c) => c.priority).map((c) => c.id));
    for (const p of this.state.peers) {
      if (!this.me || p.id === this.me.id) continue;
      for (const t of p.talk) {
        if (t === `u:${this.me.id}`) any = true;
        else if (t.startsWith('ch:') && this.me.listen.includes(t.slice(3))) {
          any = true;
          if (pri.has(t.slice(3))) priority = true;
        }
      }
    }
    return { any, priority, pri };
  }

  #gainFor(appData) {
    const act = this.#activity();
    if (appData.program === 'audio') {
      const v = this.volume('__program');
      // ducking: program drops to 25 % while someone talks to me
      return v.muted ? 0 : v.v * (act.any && this.ducking !== false ? 0.25 : 1);
    }
    const speaker = this.state.peers.find((p) => p.id === appData.speaker);
    if (!speaker || !this.me) return 0;
    let g = 0;
    for (const t of speaker.talk) {
      if (t === `u:${this.me.id}`) {
        const v = this.volume('__direct');
        g = Math.max(g, v.muted ? 0 : v.v);
      } else if (t.startsWith('ch:') && this.me.listen.includes(t.slice(3))) {
        const v = this.volume(t.slice(3));
        // a priority channel (e.g. Regie) pushes all other channels down to 30 %
        const dim = act.priority && !act.pri.has(t.slice(3)) ? 0.3 : 1;
        g = Math.max(g, v.muted ? 0 : v.v * dim);
      }
    }
    return g;
  }

  #updateGains() {
    for (const e of this.consumers.values()) {
      if (e.gain) e.gain.gain.setTargetAtTime(this.#gainFor(e.appData), this.ctx.currentTime, e.appData.program ? 0.12 : 0.01);
    }
  }

  // ---------- intercom actions ----------
  #sendTalk() {
    if (this.micTrack) this.micTrack.enabled = this.talking.size > 0;
    const targets = [...this.talking];
    // optimistic local state so the UI reacts instantly
    if (this.me) this.me.talk = targets;
    this.emit('talk', targets);
    return this.request('talk', { targets }).catch((e) => this.emit('error', e.message));
  }

  setTalk(target, on) {
    if (on && !this.micTrack) return this.emit('error', 'Joined without microphone – talking not possible');
    if (on) this.talking.add(target);
    else this.talking.delete(target);
    return this.#sendTalk();
  }

  async setListen(channelId, on) {
    const { listen } = await this.request('listen', { channelId, on });
    this.me.listen = listen;
    this.#updateGains();
    this.emit('me', this.me);
  }

  // session admin
  adminSetRole(peerId, roleId) {
    return this.request('adminSetRole', { peerId, roleId });
  }
  adminSetCam(peerId, camId) {
    return this.request('adminSetCam', { peerId, camId });
  }
  adminKick(peerId) {
    return this.request('adminKick', { peerId });
  }

  #applyBuffer(consumer) {
    const r = consumer.rtpReceiver;
    if (!r) return;
    const ms = this.programBuffer || null; // null = browser default (minimal delay)
    if ('jitterBufferTarget' in r) r.jitterBufferTarget = ms;
    else if ('playoutDelayHint' in r) r.playoutDelayHint = ms == null ? null : ms / 1000;
  }

  /** program buffer in ms (0 = minimal delay); same value for audio and video keeps them in sync */
  setProgramBuffer(ms) {
    this.programBuffer = Number(ms) || 0;
    localStorage.setItem('talk.pgmBuffer', String(this.programBuffer));
    for (const e of this.consumers.values()) if (e.appData.program || e.consumer.kind === 'video') this.#applyBuffer(e.consumer);
  }

  async setProgramVideo(quality) {
    this.videoQuality = quality === 'off' ? null : quality;
    if (this.videoConsumer) this.#closeConsumer(this.videoConsumer.consumer.id);
    await this.request('programVideo', { quality });
  }
}
